import { normalizePhotoLabels, photoLabelFreeFormId, PHOTO_LABEL_NAME_MAX_LENGTH } from "@rebuild/shared";
import type { ResponsiveNotePhotoLabel } from "@rebuild/shared/models";
import { Plus, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { useGetEstimatePhotoLabelsQuery } from "@/hooks/queries/responsive-note-photos";
import { appSelector } from "@/redux/selectors/app.selector";
import { useSelector } from "@/redux/store";
import { getPhotoLabels, isLabelablePhoto } from "./photoLabels.utils";
import type { PhotoGalleryItem } from "./photos.types";

interface PhotoPreviewLabelsProps {
  photo: PhotoGalleryItem;
  estimateId?: string;
  /** Both handlers are omitted on the legacy-file previews, which have no per-photo write path. */
  onAddLabel?: (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void;
  onRemoveLabel?: (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void;
}

export function PhotoPreviewLabels({ photo, estimateId, onAddLabel, onRemoveLabel }: PhotoPreviewLabelsProps) {
  const labels = getPhotoLabels(photo);

  // Labelability is checked per photo rather than per gallery: paging can land on a legacy file
  // inside an otherwise editable gallery, and that one has nowhere to store a label.
  if (!onAddLabel || !onRemoveLabel || !isLabelablePhoto(photo)) {
    if (labels.length === 0) {
      return null;
    }

    return (
      <ul aria-label="Photo labels" className="mt-3 flex flex-wrap justify-center gap-1.5">
        {labels.map((label) => (
          <li key={label.id}>
            <Badge variant="secondary">{label.name}</Badge>
          </li>
        ))}
      </ul>
    );
  }

  return (
    <PhotoPreviewLabelsEditor
      photo={photo}
      labels={labels}
      estimateId={estimateId}
      onAddLabel={onAddLabel}
      onRemoveLabel={onRemoveLabel}
    />
  );
}

interface PhotoPreviewLabelsEditorProps {
  photo: PhotoGalleryItem;
  labels: ResponsiveNotePhotoLabel[];
  estimateId: string | undefined;
  onAddLabel: (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void;
  onRemoveLabel: (photo: PhotoGalleryItem, label: ResponsiveNotePhotoLabel) => void;
}

function PhotoPreviewLabelsEditor({
  photo,
  labels,
  estimateId,
  onAddLabel,
  onRemoveLabel,
}: PhotoPreviewLabelsEditorProps) {
  const orgPresets = useSelector(appSelector.organizationPhotoLabels);
  const { data: estimateLabels } = useGetEstimatePhotoLabelsQuery(estimateId);
  const [isAdding, setIsAdding] = useState(false);
  const [inputValue, setInputValue] = useState("");

  // Paging to the next photo ends the add — the input would otherwise stay open over a photo the
  // user never chose to edit.
  useEffect(() => {
    setIsAdding(false);
    setInputValue("");
  }, [photo.galleryId]);

  const options = useMemo(() => {
    // Org presets first so a curated label's id and casing win the dedupe over an ad-hoc duplicate.
    const vocabulary = normalizePhotoLabels([...orgPresets, ...(estimateLabels ?? [])]);
    const appliedNames = new Set(labels.map((label) => label.name.trim().toLowerCase()));
    const unapplied = vocabulary.filter((label) => !appliedNames.has(label.name.trim().toLowerCase()));

    const typed = inputValue.trim();
    const typedKey = typed.toLowerCase();
    const isKnown = vocabulary.some((label) => label.name.trim().toLowerCase() === typedKey);
    if (!typed || isKnown || appliedNames.has(typedKey)) {
      return unapplied;
    }

    return [...unapplied, { id: `new:${typed}`, name: typed }];
  }, [estimateLabels, inputValue, labels, orgPresets]);

  const handleSelect = (selected: ResponsiveNotePhotoLabel | null) => {
    setInputValue("");
    if (!selected) return;

    const name = selected.name.trim().slice(0, PHOTO_LABEL_NAME_MAX_LENGTH);
    if (!name) return;

    // Free-form entries arrive with a placeholder id; derive the real one from the name so the same
    // word typed here and on a phone is one tag rather than two.
    onAddLabel(photo, { id: selected.id.startsWith("new:") ? photoLabelFreeFormId(name) : selected.id, name });
  };

  return (
    // min-h keeps the caption below from jumping when the last tag is removed.
    <div className="mt-3 flex min-h-8 flex-wrap items-center justify-center gap-1.5">
      <ul aria-label="Photo labels" className="contents">
        {labels.map((label) => (
          <li key={label.id}>
            <span className="bg-muted text-foreground inline-flex h-8 items-center gap-0.5 rounded-full py-0 pr-1 pl-3 text-sm font-medium">
              {label.name}
              <button
                type="button"
                aria-label={`Remove tag ${label.name}`}
                onClick={() => onRemoveLabel(photo, label)}
                className="text-muted-foreground hover:bg-foreground/10 hover:text-foreground focus-visible:ring-ring/50 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full transition-colors focus-visible:ring-3 focus-visible:outline-none"
              >
                <X className="h-3.5 w-3.5" aria-hidden="true" />
              </button>
            </span>
          </li>
        ))}
      </ul>

      {isAdding ? (
        <Combobox
          items={options}
          itemToStringValue={(option: ResponsiveNotePhotoLabel) => option.name}
          value={null}
          onValueChange={handleSelect}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
        >
          <ComboboxInput
            autoFocus
            showTrigger={false}
            placeholder="Add a tag…"
            aria-label="Add a tag to this photo"
            maxLength={PHOTO_LABEL_NAME_MAX_LENGTH}
            className="h-8 w-52"
            onKeyDown={(event) => {
              // The combobox swallows the first Escape to close its own list, so this only fires
              // once the list is already closed.
              if (event.key === "Escape") {
                setIsAdding(false);
              }
            }}
          />
          <ComboboxContent side="top" align="center" className="w-52">
            <ComboboxEmpty>Type to add a new tag</ComboboxEmpty>
            <ComboboxList className="max-h-60 overflow-y-auto">
              {(option: ResponsiveNotePhotoLabel) => (
                <ComboboxItem key={option.id} value={option}>
                  {option.id.startsWith("new:") ? `Add "${option.name}"` : option.name}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      ) : (
        <button
          type="button"
          onClick={() => setIsAdding(true)}
          className="border-input text-muted-foreground hover:border-ring hover:text-foreground focus-visible:ring-ring/50 inline-flex h-8 cursor-pointer items-center gap-1 rounded-full border border-dashed px-3 text-sm font-medium transition-colors focus-visible:ring-3 focus-visible:outline-none"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden="true" />
          Add tag
        </button>
      )}
    </div>
  );
}
